import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LayoutDashboard, ClipboardList, Syringe, Package, CalendarDays, TrendingUp, UserCircle2, ShoppingCart, ShoppingBag, Gift, LogOut, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { memberApi } from './memberApi';
import { DashboardTab } from './DashboardTab';
import { ProtocolsTab } from './ProtocolsTab';
import { InjectionsTab } from './InjectionsTab';
import { InventoryTab } from './InventoryTab';
import { CalendarTab } from './CalendarTab';
import { ProgressTab } from './ProgressTab';
import { ProfileTab } from './ProfileTab';
import { OrdersTab } from './OrdersTab';
import { RewardsTab } from './RewardsTab';
import { InstallPrompt } from './InstallPrompt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'orders', label: 'Orders', icon: ShoppingBag },
  { key: 'protocols', label: 'Protocols', icon: ClipboardList },
  { key: 'injections', label: 'Injections', icon: Syringe },
  { key: 'calendar', label: 'Calendar', icon: CalendarDays },
  { key: 'inventory', label: 'Inventory', icon: Package },
  { key: 'progress', label: 'Progress', icon: TrendingUp },
  { key: 'rewards', label: 'Rewards', icon: Gift },
  { key: 'profile', label: 'Profile', icon: UserCircle2 },
];

const BOTTOM_NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'injections', label: 'Log', icon: Syringe },
  { key: 'orders', label: 'Orders', icon: ShoppingBag },
  { key: 'rewards', label: 'Rewards', icon: Gift },
  { key: 'profile', label: 'Profile', icon: UserCircle2 },
];

export const MemberArea = ({ user, logout }) => {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [injections, setInjections] = useState([]);
  const [vials, setVials] = useState([]);
  const [peptides, setPeptides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prefill, setPrefill] = useState(null);

  const refresh = useCallback(() => {
    Promise.all([
      memberApi.get('/member/dashboard'),
      memberApi.get('/member/protocols'),
      memberApi.get('/member/injections?limit=200'),
      memberApi.get('/member/vials'),
    ]).then(([d, p, i, v]) => {
      setDashboard(d.data);
      setProtocols(p.data);
      setInjections(i.data);
      setVials(v.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
    axios.get(`${API}/peptides`).then(res => setPeptides(res.data)).catch(() => {});
  }, [refresh]);

  const handleLogNow = (protocol) => {
    setPrefill(protocol);
    setTab('injections');
  };

  return (
    <div className="min-h-[70vh] bg-[#050505] py-6 md:py-10 px-4 pb-28 md:pb-10" data-testid="member-area">
      <div className="max-w-3xl mx-auto">
        <InstallPrompt />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-['Oswald'] text-2xl md:text-3xl uppercase tracking-wide text-white">Member <span className="text-[#C41E3A]">Dashboard</span></h1>
            <p className="text-[#D4AF37] text-xs mt-1" data-testid="account-email">{user.email}</p>
          </div>
          <button onClick={logout} className="hidden md:flex items-center gap-1.5 border border-[#27272A] hover:border-[#C41E3A] text-gray-400 hover:text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors" data-testid="logout-btn">
            <LogOut size={13} /> Sign Out
          </button>
        </div>

        <div className="flex gap-1 border-b border-[#27272A] mb-6 overflow-x-auto scrollbar-none">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-3 lg:px-4 py-3 text-xs font-bold uppercase tracking-wide whitespace-nowrap transition-colors border-b-2 -mb-px flex-shrink-0 ${tab === t.key ? 'text-white border-[#C41E3A]' : 'text-gray-500 border-transparent hover:text-gray-300'}`}
              data-testid={`member-tab-${t.key}`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#C41E3A]" size={30} /></div>
        ) : (
          <>
            {tab === 'dashboard' && <DashboardTab data={dashboard} vials={vials} user={user} onLogNow={handleLogNow} onNavigate={setTab} />}
            {tab === 'orders' && <OrdersTab user={user} />}
            {tab === 'rewards' && <RewardsTab user={user} />}
            {tab === 'protocols' && <ProtocolsTab protocols={protocols} peptides={peptides} refresh={refresh} />}
            {tab === 'calendar' && <CalendarTab injections={injections} />}
            {tab === 'inventory' && <InventoryTab vials={vials} protocols={protocols} peptides={peptides} refresh={refresh} />}
            {tab === 'progress' && <ProgressTab />}
            {tab === 'profile' && <ProfileTab user={user} logout={logout} refresh={refresh} />}
            {tab === 'injections' && (
              <InjectionsTab
                injections={injections}
                protocols={protocols}
                sites={dashboard?.sites || []}
                suggestion={dashboard?.site_rotation?.suggestion || ''}
                refresh={refresh}
                prefill={prefill}
                clearPrefill={() => setPrefill(null)}
              />
            )}
          </>
        )}
      </div>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-md border-t border-[#27272A] flex" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="bottom-nav">
        {BOTTOM_NAV.map(item => item.to ? (
          <Link key={item.key} to={item.to} className="flex-1 flex flex-col items-center gap-1 py-2.5 text-gray-500" data-testid={`bottom-nav-${item.key}`}>
            <item.icon size={20} />
            <span className="text-[9px] font-bold uppercase tracking-wider">{item.label}</span>
          </Link>
        ) : (
          <button
            key={item.key}
            onClick={() => { setTab(item.key); window.scrollTo({ top: 0 }); }}
            className={`flex-1 flex flex-col items-center gap-1 py-2.5 transition-colors ${tab === item.key ? 'text-[#C41E3A]' : 'text-gray-500'}`}
            data-testid={`bottom-nav-${item.key}`}
          >
            <item.icon size={20} />
            <span className="text-[9px] font-bold uppercase tracking-wider">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};
